import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumb, Input, Modal, Select, Space } from 'antd'
import PropTypes from 'prop-types'

import {
  carRequestsUrl,
  errorNotification,
  formatUrl,
  getRequest,
  patchRequest,
  successNotification
} from '../../config'
import FilterTable from '../../components/Forms/searchFilter'
import PageLoader from '../../components/Layouts/PageLoader'
import RequestBody from './RequestBody'
import { columns, STATUS_OPTIONS } from './tableColumns'
import PageTitle from 'components/PageTitle'

const { Search } = Input

/**
 * The demand queue: what customers asked us to find, and how much they have ready.
 *
 * Ordering by budget as well as by date is deliberate. The newest request is not the most valuable
 * one, and "who has the biggest budget waiting" is the question this list actually gets asked.
 */
const CarRequestsHome = () => {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ count: 0, next: null, previous: null })
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(undefined)
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('-created_at')
  const [quickViewId, setQuickViewId] = useState(null)

  const load = useCallback(
    async customUrl => {
      let url = customUrl ? formatUrl(customUrl) : `${carRequestsUrl}/`
      if (!customUrl) {
        const params = new URLSearchParams()
        if (status) params.append('status', status)
        if (search) params.append('search', search)
        if (ordering) params.append('ordering', ordering)
        const qs = params.toString()
        if (qs) url = `${url}?${qs}`
      }
      const response = await getRequest(url, 'car requests', setLoading)
      if (response.success) {
        setRows(response.data.results)
        setMeta({
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous
        })
      } else if (response.error.response) {
        errorNotification(response.error.response.data.message)
      }
    },
    [status, search, ordering]
  )

  useEffect(() => {
    load()
  }, [load])

  /**
   * Change a status from the table.
   *
   * Optimistic, and reverted visibly on failure — a status that silently did not save means two
   * people ring the same customer, which is the exact thing the field exists to prevent.
   */
  const changeStatus = async (record, value) => {
    const previous = record.status
    setRows(current =>
      current.map(row => (row.id === record.id ? { ...row, status: value } : row))
    )
    const response = await patchRequest(
      `${carRequestsUrl}/${record.id}/`,
      { status: value },
      'status',
      () => {}
    )
    if (response.success) {
      successNotification('Status updated')
    } else {
      setRows(current =>
        current.map(row => (row.id === record.id ? { ...row, status: previous } : row))
      )
      errorNotification('Could not update the status')
    }
  }

  /** Keeps the row behind the quick-view modal in step, without refetching the page. */
  const applyUpdate = updated => {
    setRows(current =>
      current.map(row => (row.id === updated.id ? { ...row, ...updated } : row))
    )
  }

  return (
    <PageLoader className="container-fluid" loading={loading}>
      <PageTitle title="Car requests" />
      <div className="row">
        <div className="col-md-12">
          <Breadcrumb>
            <Breadcrumb.Item href="/">
              <span>Home</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Car Requests</Breadcrumb.Item>
          </Breadcrumb>
          <br />
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
              <span>Car Requests {meta && `(${meta.count})`}</span>
              <Space size={8} wrap>
                <Search
                  allowClear
                  placeholder="Search what they asked for"
                  onSearch={setSearch}
                  style={{ width: 240 }}
                />
                <Select
                  allowClear
                  placeholder="Status"
                  value={status}
                  onChange={setStatus}
                  options={STATUS_OPTIONS}
                  style={{ width: 140 }}
                />
                <Select
                  value={ordering}
                  onChange={setOrdering}
                  style={{ width: 180 }}
                  options={[
                    { value: '-created_at', label: 'Newest first' },
                    { value: 'created_at', label: 'Oldest first' },
                    { value: '-budget_ghs', label: 'Biggest budget' },
                    { value: 'budget_ghs', label: 'Smallest budget' }
                  ]}
                />
              </Space>
            </div>
            <div className="card-body">
              <FilterTable
                dataSource={rows}
                columns={columns(setQuickViewId, changeStatus)}
                meta={meta}
                getData={url => load(url)}
              />

              {/* destroyOnClose matters: the body fetches on mount, so reopening a row shows its
                  current status rather than what it held when the modal was first opened. */}
              <Modal
                title="Car Request"
                open={!!quickViewId}
                onCancel={() => setQuickViewId(null)}
                footer={null}
                width={720}
                style={{ top: 16 }}
                destroyOnClose
              >
                {quickViewId ? (
                  <RequestBody id={quickViewId} mode="modal" onUpdated={applyUpdate} />
                ) : null}
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </PageLoader>
  )
}

CarRequestsHome.propTypes = {
  history: PropTypes.object
}

export default CarRequestsHome
